import { create } from 'zustand';
import { createComponentLogger } from '@/lib/utils/logger';

const logger = createComponentLogger('Config');

/**
 * Application configuration structure
 */
export interface AppConfig {
  version: string;
  appName: string;
  appFullName: string;
  appTagline: string;
  logo?: {
    enabled: boolean;
    path: string;
    alt: string;
    height?: number;
    width?: number;
  };
  labels: {
    loginTitle: string;
    loginSubtitle?: string;
    invalidLogin: string;
    consultationTitle: string;
    summaryTitle: string;
    recordsTitle: string;
    patientFormTitle: string;
    [key: string]: string | undefined;
  };
  summary: {
    sections: {
      [sectionKey: string]: {
        visible: boolean;
        order: number;
        label: string;
        editable?: boolean;
      };
    };
  };
  features: {
    cdss: boolean;
    liveTranscription: boolean;
    audioRecording: boolean;
    patientPhotos: boolean;
    [key: string]: boolean;
  };
  vitals?: {
    fields: Array<{
      key: string;
      label: string;
      unit: string;
      type: 'number' | 'string' | 'select';
      required?: boolean;
      options?: string[];
    }>;
  };
  aiBlocks?: {
    enabled: boolean;
    suggestions?: {
      enabled: boolean;
      sections: string[];
    };
  };
  /**
   * Project-specific patient identifier configuration.
   * When set, enables book number / OPD number search and creation.
   * Leave undefined for projects that do not use a secondary identifier.
   */
  patientIdentifier?: {
    /** FHIR identifier system URL (e.g. "https://api.ehrs.swecha.org/book-no") */
    system: string;
    /** Human-readable label shown in the UI (e.g. "Book No.") */
    label: string;
    /**
     * Optional list of Medplum project IDs this identifier config applies to.
     * If omitted, the config applies to all projects.
     * IDs should be bare UUIDs (without "Project/" prefix).
     */
    projectIds?: string[];
  };
}

/**
 * Default configuration (fallback if loading fails)
 */
const defaultConfig: AppConfig = {
  version: '1.0.0',
  appName: 'Pranik.AI',
  appFullName: 'Pranik Clinical Decision Support System',
  appTagline: 'AI-powered medical consultation platform',
  labels: {
    loginTitle: 'Welcome to HMIS',
    loginSubtitle: 'Sign in to continue',
    invalidLogin: 'Invalid email or password',
    consultationTitle: 'AI-Assisted Consultation',
    summaryTitle: 'Case Sheet Summary',
    recordsTitle: 'Patient Records',
    patientFormTitle: 'Patient Registration',
  },
  summary: {
    sections: {
      demographics: {
        visible: true,
        order: 1,
        label: 'Patient Demographics',
        editable: false,
      },
      vitals: {
        visible: true,
        order: 2,
        label: 'Vital Signs',
        editable: true,
      },
      chiefComplaint: {
        visible: true,
        order: 3,
        label: 'Chief Complaint',
        editable: true,
      },
      history: {
        visible: true,
        order: 4,
        label: 'History of Present Illness',
        editable: true,
      },
      examination: {
        visible: true,
        order: 5,
        label: 'Physical Examination',
        editable: true,
      },
      diagnosis: {
        visible: true,
        order: 6,
        label: 'Diagnosis',
        editable: true,
      },
      investigations: {
        visible: true,
        order: 7,
        label: 'Investigations',
        editable: true,
      },
      treatment: {
        visible: true,
        order: 8,
        label: 'Treatment Plan',
        editable: true,
      },
      medications: {
        visible: true,
        order: 9,
        label: 'Medications',
        editable: true,
      },
      followUp: {
        visible: true,
        order: 10,
        label: 'Follow-up',
        editable: true,
      },
    },
  },
  features: {
    cdss: true,
    liveTranscription: true,
    audioRecording: true,
    patientPhotos: false,
  },
  vitals: {
    fields: [
      {
        key: 'bloodPressure',
        label: 'Blood Pressure',
        unit: 'mmHg',
        type: 'string',
        required: true,
      },
      { key: 'heartRate', label: 'Heart Rate', unit: 'bpm', type: 'number', required: true },
      { key: 'temperature', label: 'Temperature', unit: '°F', type: 'number', required: true },
      {
        key: 'respiratoryRate',
        label: 'Respiratory Rate',
        unit: 'breaths/min',
        type: 'number',
        required: true,
      },
      {
        key: 'oxygenSaturation',
        label: 'Oxygen Saturation',
        unit: '%',
        type: 'number',
        required: true,
      },
      { key: 'bloodSugar', label: 'Blood Sugar', unit: 'mg/dL', type: 'number', required: false },
    ],
  },
  aiBlocks: {
    enabled: true,
    suggestions: {
      enabled: true,
      sections: ['diagnosis', 'investigations', 'treatment', 'medications'],
    },
  },
  // patientIdentifier is intentionally undefined in the default config.
  // Specific deployments set this in public/config/app.json.
};

/**
 * Configuration store state
 */
interface ConfigState {
  /**
   * Current application configuration
   */
  config: AppConfig;

  /**
   * Whether configuration has been loaded
   */
  isLoaded: boolean;

  /**
   * Loading state
   */
  isLoading: boolean;

  /**
   * Error during configuration load
   */
  error: string | null;
}

/**
 * Configuration store actions
 */
interface ConfigActions {
  /**
   * Load configuration from public/config/app.json
   */
  loadConfig: () => Promise<void>;

  /**
   * Update configuration (for testing/overrides)
   */
  updateConfig: (config: Partial<AppConfig>) => void;

  /**
   * Reset to default configuration
   */
  resetConfig: () => void;
}

/**
 * Combined config store type
 */
type ConfigStore = ConfigState & ConfigActions;

/**
 * Configuration Store
 *
 * Manages application configuration loaded from public/config/app.json.
 * Configuration is loaded on app initialization and provides:
 * - UI labels and text
 * - Feature flags
 * - Section visibility/ordering
 * - Vital signs fields
 * - AI block settings
 *
 * @example
 * ```tsx
 * // Load configuration
 * const { loadConfig, config, isLoaded } = useConfigStore();
 *
 * useEffect(() => {
 *   loadConfig();
 * }, [loadConfig]);
 *
 * if (!isLoaded) return <div>Loading...</div>;
 *
 * // Use configuration
 * <h1>{config.labels.loginTitle}</h1>
 *
 * // Check feature flag
 * {config.features.cdss && <CDSSBlock />}
 *
 * // Get section config
 * const diagnosisSection = config.summary.sections.diagnosis;
 * if (diagnosisSection.visible) {
 *   // Render section
 * }
 * ```
 */
export const useConfigStore = create<ConfigStore>((set, get) => ({
  // Initial state
  config: defaultConfig,
  isLoaded: false,
  isLoading: false,
  error: null,

  // Actions
  loadConfig: async () => {
    // Don't reload if already loaded
    if (get().isLoaded) return;

    set({ isLoading: true, error: null });

    try {
      const response = await fetch('/config/app.json');

      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.statusText}`);
      }

      const config = await response.json();

      // Merge with default config to ensure all fields exist
      const mergedConfig: AppConfig = {
        ...defaultConfig,
        ...config,
        labels: {
          ...defaultConfig.labels,
          ...config.labels,
        },
        summary: {
          sections: {
            ...defaultConfig.summary.sections,
            ...config.summary?.sections,
          },
        },
        features: {
          ...defaultConfig.features,
          ...config.features,
        },
        vitals: config.vitals || defaultConfig.vitals,
        aiBlocks: {
          ...defaultConfig.aiBlocks,
          ...config.aiBlocks,
        },
        // patientIdentifier: pass through from config if present (undefined = disabled)
        patientIdentifier: config.patientIdentifier || undefined,
      };

      set({
        config: mergedConfig,
        isLoaded: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      logger.error('Failed to load configuration:', error);

      set({
        config: defaultConfig,
        isLoaded: true, // Mark as loaded even on error (using defaults)
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load configuration',
      });
    }
  },

  updateConfig: (configUpdate) => {
    set((state) => ({
      config: {
        ...state.config,
        ...configUpdate,
      },
    }));
  },

  resetConfig: () => {
    set({
      config: defaultConfig,
      isLoaded: false,
      isLoading: false,
      error: null,
    });
  },
}));

/**
 * Selectors for common config queries
 */
export const configSelectors = {
  /**
   * Get entire config
   */
  config: (state: ConfigStore) => state.config,

  /**
   * Get labels
   */
  labels: (state: ConfigStore) => state.config.labels,

  /**
   * Get features
   */
  features: (state: ConfigStore) => state.config.features,

  /**
   * Get summary sections
   */
  summarySections: (state: ConfigStore) => state.config.summary.sections,

  /**
   * Get loading state
   */
  isLoading: (state: ConfigStore) => state.isLoading,

  /**
   * Get error
   */
  error: (state: ConfigStore) => state.error,
};

/**
 * Returns patientIdentifier config only when it applies to the supplied project reference.
 * Pass the value from localStorage('project-reference') — e.g. "Project/<uuid>" or "<uuid>".
 * Returns undefined when the feature should be hidden for the current project.
 */
export function resolvePatientIdentifier(
  config: AppConfig,
  projectReference?: string | null
): AppConfig['patientIdentifier'] | undefined {
  if (!config.patientIdentifier) return undefined;
  const { projectIds } = config.patientIdentifier;
  if (!projectIds || projectIds.length === 0) return config.patientIdentifier;
  const projectId = (projectReference || '').replace('Project/', '');
  return projectIds.includes(projectId) ? config.patientIdentifier : undefined;
}

/**
 * Helper to check if a feature is enabled
 */
export function isFeatureEnabled(feature: keyof AppConfig['features']): boolean {
  return useConfigStore.getState().config.features[feature];
}

/**
 * Helper to get a label by key
 */
export function getLabel(key: keyof AppConfig['labels']): string {
  return useConfigStore.getState().config.labels[key] || '';
}

/**
 * Helper to get visible sections sorted by order
 */
export function getVisibleSections() {
  const sections = useConfigStore.getState().config.summary.sections;

  return Object.entries(sections)
    .filter(([_, section]) => section.visible)
    .sort(([_, a], [__, b]) => a.order - b.order)
    .map(([key, section]) => ({ key, ...section }));
}
